import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { ReportTranslationKeys } from 'intl/keys';
import {
  MemoStatefulMultiselect,
  MemoStatefulSingleSelect,
} from 'components/dataCreationForms/withInputForm/statefulInputsWrapper';
import { useVisibleFieldsByProcess } from 'store/visibleFields';
import { MultiChoiceFormFieldProps } from 'components/inspection/Form/types';
import {
  REPORT_FIELDS_CONTENT_LABELS,
  ReportFieldsContent,
} from 'shared/domain/report/types/model/common';
import { createAvailableFieldsSet } from 'shared/domain/visibleField/createAvailableFieldsSet';
import { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useProcesses } from 'store/process';
import { getAvailableFields } from 'shared/domain/visibleField/availableFields';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { LabelledEntity } from 'shared/types/commonView';

export const REPORT_DEFAULT_EXCLUDED_FIELD_NAMES: IssueFieldNames[] = [
  IssueFieldNames.level,
  IssueFieldNames.site,
  IssueFieldNames.stage,
  IssueFieldNames.issueNumber,
  IssueFieldNames.title,
  IssueFieldNames.documents,
  IssueFieldNames.linkedDocumentation,
  IssueFieldNames.hashtag,
];

export function ReportFieldsContentSelect(): ReactElement {
  const intl = useIntl();
  const reportFieldsTypeProps: MultiChoiceFormFieldProps<{
    fieldsContent: string;
  }> = useMemo(
    () => ({
      formKey: 'fieldsContent',
      labelId: ReportTranslationKeys.fieldContentLabel,
      required: true,
      fieldName: 'select-report-fields-content',
      available: Object.values(ReportFieldsContent).map(
        (key: ReportFieldsContent) => ({
          _id: key,
          label: formatTranslation(intl, unsafeTranslationKey(REPORT_FIELDS_CONTENT_LABELS[key]),),
        })
      ),
      getOptionLabel: (option) => option.label,
      getOptionSelected: (
        option: LabelledEntity,
        value: LabelledEntity
      ): boolean => option._id === value._id,
      minRows: 3,
      entityName: '',
      dense: false,
      reserveSpaceForHelperText: false,
      'data-qa': 'report_fields_content',
      issueParams: null,
    }),
    [intl]
  );

  return <MemoStatefulSingleSelect {...reportFieldsTypeProps} />;
}

export function ReportSelectedFieldsMultiselect({
  excludedFieldNames = REPORT_DEFAULT_EXCLUDED_FIELD_NAMES,
}: {
  excludedFieldNames?: IssueFieldNames[];
} = {}): ReactElement | null {
  const intl = useIntl();
  const { processes } = useProcesses();
  const { values } = useInputForm();
  const visibleFields = useVisibleFieldsByProcess();
  const visibleFieldsSet = useMemo(
    () =>
      createAvailableFieldsSet(
        visibleFields,
        getAvailableFields().filter(
          (field) => !field.canBeDisabled
        ),
        processes
      ),
    [processes, visibleFields]
  );
  const fieldsMap = useMemo(
    () =>
      getAvailableFields().reduce((result, field) => {
        result[field.fieldName] = field;
        return result;
      }, {}),
    []
  );

  const reportFields = useMemo(() => {
    return {
      formKey: 'selectedFields',
      required: false,
      labelId: ReportTranslationKeys.selectedFieldsLabel,
      fieldName: 'multiselect-report-selected-fields',
      maxVisibleTags: Number.MAX_SAFE_INTEGER,
      available: Array.from(visibleFieldsSet)
        .filter(
          (fieldName) =>
            !excludedFieldNames.includes(fieldName as IssueFieldNames)
        )
        .map((field) => {
          return {
            _id: field,
            label: formatTranslation(intl, unsafeTranslationKey(fieldsMap[field].labelId),),
          };
        })
        .sort((a, b) => {
          return a.label.localeCompare(b.label);
        }),
    };
  }, [visibleFieldsSet, intl, fieldsMap, excludedFieldNames]);

  if (!values.fieldsContent) {
    return null;
  }

  if (values.fieldsContent._id !== ReportFieldsContent.selected) {
    return null;
  }

  return <MemoStatefulMultiselect issueParams={null} {...reportFields} />;
}
